/**
 * Convenient hook for constructing URLs to the normal fs domain.
 * Handles state and encoding other parameters.
 * Ensures unused parameters aren't included in the URL to keep links tidy.
 */
import { useMemo } from 'react'
import { baseUrl } from '@fs/zion-config'

export default function useSiteLink({ pathname, customState, forgotPassword, username, returnUrl }) {
  return useMemo(() => {
    const params = new URLSearchParams()
    if (forgotPassword) {
      params.set('password', 'true')
    }
    if (username) {
      params.set('username', username)
    }
    const state = customState || getStateUrl()
    if (state) {
      params.set('state', state)
    }
    if (returnUrl && !state) {
      params.set('returnUrl', returnUrl)
    }
    const query = params.toString()
    return `${baseUrl}${pathname}${query ? `?${query}` : ''}`
  }, [pathname, customState, forgotPassword, returnUrl, username])
}

/**
 * Gets the URL used for "state" which is what auth app (auth-middleware) passes so it can keep track of the user's desired destination after login.
 *
 * Unlike recovery or registration the backend keeps track of the state URL internally
 * so it only need to be used in the login UI in places where we are linking outside the login flow
 * (i.e. to recovery or registration)
 */
function getStateUrl() {
  try {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get('state')
  } catch (error) {
    // not likely but theoretically possible (e.g. URL/URLSearchParams issue)
  }
  return null
}
